import { useLayoutEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { SEOHead, getArticleSchema, getBreadcrumbListSchema } from '@/components/base/SEOHead';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const article = getArticleById(id || '');

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-foreground-500 mb-4">该文章可能已被删除或不存在</p>
          <Link to="/" className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article, 3);
  const pageUrl = `https://www.51heiliao.com/article/${article.id}`;

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      getArticleSchema({
        title: article.title,
        description: article.excerpt,
        url: pageUrl,
        imageUrl: article.coverImage,
        publishDate: article.publishDate,
        author: article.author,
      }),
      getBreadcrumbListSchema([
        { name: '首页', url: 'https://www.51heiliao.com/' },
        { name: article.categoryName, url: `https://www.51heiliao.com/category/${article.category}` },
        { name: article.title, url: pageUrl },
      ]),
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 五一黑料网`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={pageUrl}
        ogType="article"
        ogImage={article.coverImage}
        schema={pageSchema}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 md:pt-24">
          <article>
            {/* Breadcrumb */}
            <div className="px-4 md:px-6 py-3">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.categoryName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>

            {/* Article Header */}
            <header className="px-4 md:px-6 py-4 md:py-6 max-w-4xl mx-auto">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-accent-500/10 text-accent-500 whitespace-nowrap">
                  {article.categoryName}
                </span>
                {article.isHot && (
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-primary-500/10 text-primary-500 whitespace-nowrap">
                    热门
                  </span>
                )}
                {article.isExclusive && (
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-secondary-500/10 text-secondary-600 whitespace-nowrap">
                    独家爆料
                  </span>
                )}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="whitespace-nowrap">
                  <i className="ri-user-line mr-1"></i>{article.author}
                </span>
                <span className="whitespace-nowrap">
                  <i className="ri-calendar-line mr-1"></i>{article.publishDate}
                </span>
                <span className="whitespace-nowrap">
                  <i className="ri-eye-line mr-1"></i>{(article.readCount / 10000).toFixed(1)}万 阅读
                </span>
                <span className="whitespace-nowrap">
                  <i className="ri-chat-3-line mr-1"></i>{article.commentCount} 评论
                </span>
              </div>
            </header>

            {/* Cover Image */}
            <div className="px-4 md:px-6 mb-6 max-w-4xl mx-auto">
              <div className="rounded-lg overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-auto object-cover max-h-[500px]"
                />
              </div>
            </div>

            {/* Article Content */}
            <div className="px-4 md:px-6 pb-8 max-w-3xl mx-auto">
              <div
                className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-950 prose-p:text-foreground-700 prose-p:leading-relaxed prose-h2:text-xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-4 prose-p:mb-4"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="mt-8 pt-6 border-t border-background-300/40">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-300/40 whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="px-4 md:px-6 pb-10" aria-label="相关文章">
              <div className="max-w-6xl mx-auto">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5 md:mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.map((rel) => (
                    <ArticleCard key={rel.id} article={rel} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Back to Home */}
          <div className="px-4 md:px-6 pb-10 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-300/40 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回首页查看更多
            </Link>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}